import { Timestamp } from '@bufbuild/protobuf';
import {
  HomeworkTimer,
  IQuestionStatus,
  IQuestionStatusObject,
  IStyles,
  QuestionContainerWithSolution,
  ProctoringConfig,
  pxToRem,
  pxTovW,
  deserify,
  useCommonServiceClientContext,
  useProctoring,
  getLastSignificantState,
  cloneObject,
  ActionPopUpSingleButton,
  ActionsPopup,
  IconWrapper,
  LinkButton,
  Loader,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  AssessmentContent,
  // AssessmentQuestionSequenceInfo_TaskQuestionInfo,
} from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssignmentConfigEnum,
  ContentAttempt,
  IdleTimeInformation,
  MarkingSchemeEnum,
  MultiSubjectSection,
  StudentAssessmentResponse,
  TaskQuestionInfo,
  TaskStudentAttemptStatusEnum,
  TimeStampWithKey,
} from '@protos/learning_management/lms.db_pb';
import {
  AssessmentStudentResponse,
  AssessmentStudentResponseV2,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from 'apps/student/src/app/Context/GlobalContextProvider';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/student/src/reduxStore/reduxHooks';
import { useDownloadContext } from 'apps/student/src/app/Context/DownloadContextProviderV2';
// import { interactionEvent } from '../../Auth/Login/login_logout.events';
// import { checkFeatureData } from '../utils_homework/homeworkUtil';
import QuestionsSummaryPagination from '../../ActiveHw/component/QuestionsSummaryPagination';
import { ProctoringPopups } from '../../ActiveHw/component/ProctoringPopups';
import { setProctoringViolationsSummary } from '../../reducer/homework.slice';
import { interactionEvent } from '../../../Auth/Login/login_logout.events';
import { checkFeatureData } from '../../utils_homework/homeworkUtil';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import {
  setActiveAssessmentAttemptResult,
  setActiveAssessmentSessionId,
  setActiveAssessmentStudentResponse,
  setActiveSectionNumber,
  setAllAnswers,
  setSelectedAssessmentData,
} from '../reducer/multisubjectFA.slice';
import {
  FA_SUBMITTED,
  HOME,
  HOMEWORK_STUDENT_DASHBOARD,
  HOMEWORK_SUBMITTED,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import { EndTestPopupContent } from '../../ActiveHw/component/ActiveHomework';
import { onHomeworkCardClick } from 'apps/student/src/utils/homework';
import ActiveProgressbar from '../../ActiveHw/component/ActiveProgressbar';
import ImageUploadPopup from '../../ActiveHw/component/ImageUploadPopup';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { OverallSummaryPopup } from '../components/OverallSummaryPopup';
import {
  getSubjectNameFromEnum,
  getSummaryData,
} from '../../utils_assessment/assessmentUtils';

const styles: IStyles = {
  root: {
    position: 'relative',
    boxSizing: 'border-box',
    p: {
      xs: `${pxToRem(15)} 0`,
      md: pxToRem(20),
      lg: `${pxTovW(40)} ${pxTovW(240)}`,
    },
    display: 'flex',
    justifyContent: 'space-between',
    m: 0,
  },
  heading: {
    mb: { xs: pxToRem(20), md: pxTovW(20) },
    px: { xs: pxToRem(8), md: 0 },
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  questionNumberBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: pxTovW(15),
    p: { xs: `${0} ${pxToRem(4)}`, md: `${pxTovW(20)} ${pxTovW(20)}` },
    boxSizing: 'border-box',
    mx: { xs: pxToRem(4), md: 0 },
    borderRadius: { xs: pxToRem(8), md: 'none' },
  },
  questionBox: {
    margin: 'auto',
    mt: { xs: pxToRem(20), md: pxTovW(0) },
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(8)}`,
    },
    boxSizing: 'border-box',
  },
  submitButtonContainer: {
    margin: 'auto',
    // width: 'max-content',
    // mt: { xs: pxToRem(20), md: pxTovW(30) },
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(8)}`,
      md: `${pxTovW(20)} ${pxTovW(0)}`,
    },
    width: '100%',
    // width: { md: pxTovW(800), lg: pxTovW(600) },
    boxSizing: 'border-box',
  },
  submitButton: {
    background: '#FFE8E8',
    width: '100%',
    color: '#F54040',
    border: '1px solid #F54040',
    height: { xs: pxToRem(49), md: pxTovW(81), lg: pxTovW(61) },
    '&:hover': { background: '#FFD6D6' },
  },
};

const getCompletionPercentage = (
  questionStatusArray: IQuestionStatusObject[]
) => {
  if (!questionStatusArray.length) return 0;
  const attempted = questionStatusArray.filter(
    (q) => q.statusInfo === IQuestionStatus.Attempted
  ).length;
  return Math.round((attempted * 100) / questionStatusArray.length);
};

const buildSortedQuestionIds = (
  questionsInfo?: TaskQuestionInfo[]
): string[] => {
  if (!questionsInfo?.length) return [];
  return questionsInfo
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((val) => val.questionId)
    .filter((val): val is string => !!val);
};

const FAActiveHomework: React.FC = () => {
  const NON_SEQUENTIAL = true;
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pathSegments = location.pathname.split('/');
  const {
    LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const homework_end_path = useAppSelector(
    (state) => state.homework.homework_end_path
  );
  const { isOffline } = useDownloadContext();
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchParams = new URLSearchParams(location.search);
  const assessmentId = searchParams.get('assessmentId') || undefined;
  const sessionMode = searchParams.get('sessionMode') || undefined;

  const {
    active_assessment_content,
    active_assessment_student_response,
    active_assessment_id,
    active_section_number,
    active_assessment_session_id,
    selected_assessment_data,
    all_answers,
  } = deserify(useAppSelector((state) => state.multisubjectFA));
  const { selected_resource_info } = deserify(
    useAppSelector((state) => state.learn)
  );
  const userInfo = deserify(useAppSelector((state) => state.auth.userInfo));
  const { proctoring_violations_summary } = deserify(
    useAppSelector((state) => state.homework)
  );

  const [openUploadPopup, setOpenUploadPopup] = useState<boolean>(false);
  const [timeupPopup, setTimeupPopup] = useState(false);
  const activeAssessmentContent: AssessmentContent | undefined =
    assessmentId && active_assessment_content
      ? active_assessment_content[assessmentId]
      : undefined;

  const activeAssessmentStudentResponse: AssessmentStudentResponse | undefined =
    assessmentId && active_assessment_student_response
      ? active_assessment_student_response[assessmentId]
      : undefined;

  const taskQuestionInfo =
    activeAssessmentContent?.questionsSequenceInfo?.taskQuestionInfo || [];

  const sortedQuestionIds = useMemo(
    () => buildSortedQuestionIds(taskQuestionInfo),
    [taskQuestionInfo]
  );
  const [activeQuestionId, setActiveQuestionId] = useState<string>('');
  const activeSectionNo = useMemo(() => {
    return active_section_number || 1;
  }, [active_section_number]);
  const activeQuestionIndex = useMemo(
    () => (activeQuestionId ? sortedQuestionIds.indexOf(activeQuestionId) : -1),
    [sortedQuestionIds, activeQuestionId]
  );

  console.log(activeSectionNo, 'activeSectionNo');

  const totalQuestions = sortedQuestionIds.length;

  const [questionStatusArray, setQuestionStatusArray] = useState<
    IQuestionStatusObject[]
  >(
    sortedQuestionIds.map((_, index) => ({
      index,
      statusInfo: IQuestionStatus.NotAttempted,
    }))
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startTime, setStartTime] = useState(Timestamp.fromDate(new Date()));
  const [isPaused, setIsPaused] = useState(false);
  const [endTestPopup, setEndTestPopup] = useState(false);
  const [questionSummaryPopup, setQuestionSummaryPopup] = useState(false);
  const [overallSectionPopup, setOverallSectionPopup] = useState(false);
  const selectedQuestionContent =
    activeAssessmentContent?.assessmentContent.find(
      (hw) => hw.questionId == activeQuestionId
    );
  const onBackClick = async () => {
    const studId = userInfo?.studentProfileId;
    if (studId) {
      // await aiHomeworkCloseEvent({
      //   homeworkId: selected_homework_data?.homeworkId,
      //   isOffline: isOffline,
      // });
      // if (!showMeme) {
      submitBackHandler(true);
      // }
      onHomeworkCardClick(
        dispatch,
        navigate,
        (assessmentId ? BigInt(assessmentId) : undefined),
        studId,
        undefined,
        homework_end_path,
        activeAssessmentContent?.assessment?.taskType,
        activeAssessmentContent?.assessment?.isMultiSubjectAssessmentFlow
      );
    }
  };
  const onLogoClick = async () => {
    const studId = userInfo?.studentProfileId;
    if (studId) {
      // await aiHomeworkCloseEvent({
      //   homeworkId: selected_homework_data?.homeworkId,
      //   isOffline: isOffline,
      // });
      submitBackHandler(true);
      navigate(HOME);
    }
  };
  // -------------------- Proctoring --------------------

  const isProctoringEnabled = useMemo(() => {
    const configs = activeAssessmentContent?.assessment?.assignmentInfo || [];

    return configs.some(
      (info) =>
        info.configName === AssignmentConfigEnum.ASSIGNMENT_CONFIG_PROCTORING &&
        info.isEnabled === true
    );
  }, [activeAssessmentContent]);

  const proctoringConfig: ProctoringConfig = {
    fullScreen: isProctoringEnabled,
    detectTabSwitch: isProctoringEnabled,
    blockCopyPaste: isProctoringEnabled,
    blockRightClick: isProctoringEnabled,
    blockKeyboardShortcuts: isProctoringEnabled,
    trackIdle: isProctoringEnabled,
    detectMultipleSessions: isProctoringEnabled,
  };

  console.log('section:', active_section_number);

  const [showFullScreenPopup, setShowFullScreenPopup] = useState(false);
  const [showTabSwitchPopup, setShowTabSwitchPopup] = useState(false);
  const [showMultipleSessionPopup, setShowMultipleSessionPopup] =
    useState(false);
  const [showInactiveSessionPopup, setShowInactiveSessionPopup] =
    useState(false);
  const [splitText, setSplitText] = useState<string | undefined>(undefined); //for end test toast msg
  const [clearNonce, setClearNonce] = useState(0);
  const [isLastQuestionAttempted, setIsLastQuestionAttempted] =
    useState<boolean>(false);
  const lastInputFocusTimeRef = useRef<number>(0);

  const isInputFieldActive = useCallback(() => {
    const activeElement = document.activeElement;
    if (!activeElement) return false;

    const tagName = activeElement.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable =
      activeElement.getAttribute('contenteditable') === 'true';

    if (isInput || isContentEditable) return true;

    const closestContentEditable = activeElement.closest(
      '[contenteditable="true"]'
    );
    return !!closestContentEditable;
  }, []);

  useEffect(() => {
    if (!isProctoringEnabled) return;

    const handleInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const tagName = target.tagName.toLowerCase();
      const isInput = tagName === 'input' || tagName === 'textarea';
      const isContentEditable =
        target.getAttribute('contenteditable') === 'true';
      const closestContentEditable = target.closest('[contenteditable="true"]');

      if (isInput || isContentEditable || closestContentEditable) {
        lastInputFocusTimeRef.current = Date.now();
      }
    };

    document.addEventListener('focusin', handleInputFocus, true);
    return () => {
      document.removeEventListener('focusin', handleInputFocus, true);
    };
  }, [isProctoringEnabled, isInputFieldActive]);

  function canUseFullscreen(): boolean {
    const el = document.documentElement as any;

    return (
      Boolean(
        document.fullscreenEnabled ??
          (document as any).webkitFullscreenEnabled ??
          (document as any).mozFullScreenEnabled ??
          (document as any).msFullscreenEnabled
      ) &&
      Boolean(
        el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.mozRequestFullScreen ||
          el.msRequestFullscreen
      )
    );
  }

  const proctoring = useProctoring(
    proctoringConfig,
    // ============ callbacks ============
    {
      // ----- useFullScreenEnforcement  -----
      onFullScreenExit: () => {
        // Ignore fullscreen exit if user is interacting with an input field
        // This prevents false positives when keyboard opens on mobile/tablet
        const isInputActive = isInputFieldActive();
        const recentInputFocus =
          Date.now() - lastInputFocusTimeRef.current < 500; // 500ms window

        if (isInputActive || recentInputFocus || !canUseFullscreen()) {
          return;
        }

        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            fullScreenExits: [
              ...(proctoring_violations_summary?.fullScreenExits || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
        setShowFullScreenPopup(true);
      },

      // ----- useTabSwitchDetection  -----
      onTabSwitch: (isHidden) => {
        if (isHidden) {
          // Ignore tab switch if user is interacting with an input field
          // This prevents false positives when keyboard opens on mobile/tablet
          const isInputActive = isInputFieldActive();
          const recentInputFocus =
            Date.now() - lastInputFocusTimeRef.current < 500; // 500ms window

          if (isInputActive || recentInputFocus) {
            return;
          }

          dispatch(
            setProctoringViolationsSummary({
              ...proctoring_violations_summary,
              tabSwitches: [
                ...(proctoring_violations_summary?.tabSwitches || []),
                Timestamp.fromDate(new Date()),
              ],
            })
          );
          setShowTabSwitchPopup(true);
        }
      },

      // ----- useCopyPasteRestriction  -----
      onCopyAttempt: (copyData) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            copyAttempts: [
              ...(proctoring_violations_summary?.copyAttempts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date(copyData.timestamp)),
                key: copyData.text,
              }),
            ],
          })
        );
      },
      onPasteAttempt: (pasteData) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            pasteAttempts: [
              ...(proctoring_violations_summary?.pasteAttempts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date(pasteData.timestamp)),
                key: pasteData.text,
              }),
            ],
          })
        );
      },

      // ----- useRightClickRestriction  -----
      onRightClickAttempt: () => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            rightClickAttempts: [
              ...(proctoring_violations_summary?.rightClickAttempts || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
      },

      // ----- useKeyboardShortcutBlocking  -----
      onBlockedShortcut: (key) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            blockedShortcuts: [
              ...(proctoring_violations_summary?.blockedShortcuts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date()),
                key: key,
              }),
            ],
          })
        );
      },

      // ----- useIdleTracking  -----
      onIdle: (idleTime) => {
        const now = Timestamp.fromDate(new Date());
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            idletimeData: [
              ...(proctoring_violations_summary?.idletimeData || []),
              new IdleTimeInformation({
                idleStartTime: now,
              }),
            ],
          })
        );
        setShowInactiveSessionPopup(true);
      },
      onActive: () => {
        const lastItem =
          proctoring_violations_summary?.idletimeData?.[
            proctoring_violations_summary?.idletimeData?.length - 1
          ];
        if (lastItem && !lastItem.idleEndTime) {
          const now = Timestamp.fromDate(new Date());
          dispatch(
            setProctoringViolationsSummary({
              ...proctoring_violations_summary,
              idletimeData: [
                ...(proctoring_violations_summary?.idletimeData || []).slice(
                  0,
                  -1
                ),
                new IdleTimeInformation({
                  idleStartTime: lastItem.idleStartTime,
                  idleEndTime: now,
                }),
              ],
            })
          );
        }
      },

      // ----- useMultipleSessionDetection  -----
      onMultipleSessionDetected: () => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            multipleSessionsData: [
              ...(proctoring_violations_summary?.multipleSessionsData || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
        setShowMultipleSessionPopup(true);
      },
    },
    {
      idleConfig: {
        idleThreshold: 60000,
        checkInterval: 1000,
      },
    }
  );
  const submitProctoringData = async () => {
    if (!proctoring_violations_summary) return;

    try {
      const proctoringResponse =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.createProctoringLog(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            homeworkSessionId: active_assessment_session_id,
            questionId: activeQuestionId,
            fullScreenExits:
              proctoring_violations_summary?.fullScreenExits || [],
            tabSwitches: proctoring_violations_summary?.tabSwitches || [],
            copyAttempts: proctoring_violations_summary?.copyAttempts || [],
            pasteAttempts: proctoring_violations_summary?.pasteAttempts || [],
            rightClickAttempts:
              proctoring_violations_summary?.rightClickAttempts || [],
            blockedShortcuts:
              proctoring_violations_summary?.blockedShortcuts || [],
            idleTimeData: proctoring_violations_summary?.idletimeData || [],
            multipleSessions:
              proctoring_violations_summary?.multipleSessionsData || [],
          }
        );
      if (proctoringResponse.status === 200) {
        dispatch(setProctoringViolationsSummary(undefined));
      }
    } catch (error) {
      console.log(error);
    }
  };
  const exitFullScreen = useCallback(() => {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen:', err);
      });
    }
  }, []);

  // -------------------- Timer --------------------

  // const initialTimerValue = useMemo(() => {
  //   return (
  //     (activeAssessmentContent?.assessment?.estimatedTimeInMin || 0) * 60 -
  //     (activeAssessmentStudentResponse?.attemptSpentTimeInSecs || 0)
  //   );
  // }, [activeAssessmentContent, activeAssessmentStudentResponse]);
  const initialTimerValue = useMemo(() => {
    const total =
      (activeAssessmentContent?.assessment?.estimatedTimeInMin || 0) * 60;

    const spent = activeAssessmentStudentResponse?.attemptSpentTimeInSecs || 0;

    const reattempt = checkFeatureData({
      assignmentConfigInfo: activeAssessmentContent?.assessment?.assignmentInfo,
      userInfo,
    }).reattempt;
    const status = activeAssessmentStudentResponse?.assessmentAttemptStatus;

    const isTimedOutOrCompleted =
      status === TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_TIMED_OUT ||
      status === TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED;

    return reattempt && isTimedOutOrCompleted ? total : total - spent;
  }, [activeAssessmentContent, activeAssessmentStudentResponse, userInfo]);

  const scanClickHandler = async () => {
    setOpenUploadPopup(true);
    // await interactionEvent({
    //   url: '',
    //   context: 'homework',
    //   name: 'SCAN',
    //   pathSegments: pathSegments,
    //   isOffline: isOffline,
    // });
  };
  const handleTimeUpdate = (remainingTime: number) => {
    if (remainingTime < 0) {
      return;
    }
    if (activeAssessmentContent?.assessment?.estimatedTimeInMin) {
      const oldValues = cloneObject(selected_assessment_data);
      const newElapsedTimeinSecs =
        activeAssessmentContent?.assessment?.estimatedTimeInMin * 60 -
        remainingTime;
      if (
        newElapsedTimeinSecs <=
        activeAssessmentContent?.assessment?.estimatedTimeInMin * 60
      ) {
        const cloneActiveHomeworkStudentResp = new AssessmentStudentResponseV2(
          cloneObject(activeAssessmentStudentResponse)
        );
        cloneActiveHomeworkStudentResp.attemptSpentTimeInSecs =
          newElapsedTimeinSecs;
        dispatch(
          setActiveAssessmentStudentResponse({
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            response: cloneActiveHomeworkStudentResp,
          })
        );
        dispatch(
          setSelectedAssessmentData({
            ...oldValues,
            elapsedTime: newElapsedTimeinSecs, // Update the elapsed time in Redux
          })
        );
      }
    }
  };

  // -------------------- Answer handling --------------------

  // const selectedQuestion = useMemo(() => {
  //   if (!activeQuestionId) return undefined;
  //   return activeAssessmentContent?.assessmentContent.find(
  //     (hw) => hw.questionId === activeQuestionId
  //   );
  // }, [activeAssessmentContent, activeQuestionId]);

  const handleAnswerChange = (val: string, ind?: number) => {
    // let answersCopy = structuredClone(all_answers);
    let answersCopy = cloneObject(all_answers);
    switch (selectedQuestionContent?.questionType) {
      case Question_QuestionEnum.QUESTION_TYPE_MCQM:
        if (!answersCopy.includes(val)) {
          answersCopy.push(val);
        } else {
          answersCopy = answersCopy.filter((v) => v !== val);
        }
        break;
      case Question_QuestionEnum.QUESTION_TYPE_FIB:
        if (typeof ind == 'number') {
          if (ind >= 1) {
            for (let i = 0; i < ind; i++) {
              if (!answersCopy[i]) {
                answersCopy[i] = '';
              }
            }
          }
          answersCopy[ind] = val;
        }
        break;
      case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      case Question_QuestionEnum.QUESTION_TYPE_TF:
        answersCopy = [val];
        break;
      case Question_QuestionEnum.QUESTION_TYPE_LONG:
      case Question_QuestionEnum.QUESTION_TYPE_SHORT:
      case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
      case Question_QuestionEnum.QUESTION_TYPE_CBQ:
        answersCopy = [val];
        break;
    }
    // setAnswers(answersCopy);
    dispatch(setAllAnswers(answersCopy));
    // Move to next question if tf or mcq-s
    switch (selectedQuestionContent?.questionType) {
      case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      case Question_QuestionEnum.QUESTION_TYPE_TF:
        setIsSubmitting(true);
        break;
      default:
        break;
    }
  };

  const handleNonSeqNext = async (skip?: boolean, fromPagination?: boolean) => {
    await submitProctoringData();

    if (!assessmentId || isNaN(Number(assessmentId))) {
      return;
    }
    if (!active_assessment_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }

    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      activeAssessmentStudentResponse
    );
    const responses = sortedQuestionIds.map((questionId) => {
      const existingResponse = activeHomeworkStudentResp.responses.find(
        (resp) => resp.questionId === questionId
      );
      const sectionNo = taskQuestionInfo.find(
        (val) => val.questionId === questionId
      )?.sectionNo;
      if (existingResponse) {
        if (!existingResponse.sectionNo) {
          existingResponse.sectionNo = sectionNo;
        }
        return existingResponse;
      } else {
        const questionTaskId = taskQuestionInfo.find(
          (val) => val.questionId === questionId
        )?.questionTaskId;
        return new StudentAssessmentResponse({
          questionId: questionId,
          questionTaskId: questionTaskId,
          responses: [],
          isAttempted: false,
          sectionNo: sectionNo,
        });
      }
    });

    const lastValidAnswer = getLatestAnswer(activeQuestionId);
    if (skip) {
      dispatch(setAllAnswers([]));
    }

    const quesAttempt = new ContentAttempt({
      startTime: startTime,
      endTime: Timestamp.fromDate(new Date()),
      answer: skip ? [] : all_answers.length > 0 ? all_answers : [],
    });

    const activeQuestionResponseIndex = responses.findIndex(
      (resp) => resp.questionId === activeQuestionId
    );
    const updatedStatusArray = [...questionStatusArray];
    if (activeQuestionResponseIndex >= 0) {
      responses[activeQuestionResponseIndex].responses.push(quesAttempt);
      if (quesAttempt.answer.length > 0) {
        console.log('Marking question as attempted', { quesAttempt });
        responses[activeQuestionResponseIndex].isAttempted = true;
        //updating ques status array
        updatedStatusArray[activeQuestionIndex] = {
          ...updatedStatusArray[activeQuestionIndex],
          statusInfo: IQuestionStatus.Attempted,
        };
      } else {
        // if there is even one valid answer in previous attempts then mark attempted
        console.log(
          'No answer provided in this attempt. Checking for last valid answer.',
          { quesAttempt, lastValidAnswer }
        );
        responses[activeQuestionResponseIndex].isAttempted = lastValidAnswer
          ? true
          : false;
      }
    }
    const timeDeltaInSeconds = Math.floor(
      (new Date().getTime() - startTime.toDate().getTime()) / 1000
    );

    const updatedElapsedTime = selected_assessment_data?.elapsedTime || 0;

    try {
      setIsPaused(true);
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
            responses: responses,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_assessment_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeAssessmentContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_assessment_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse.status === 200) {
        setQuestionStatusArray(updatedStatusArray);
        // const studentHomeworkResp =
        //   await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
        //     {
        //       studentId: userInfo?.studentProfileId,
        //       assessmentId: (homework_id ? BigInt(homework_id) : undefined),
        //     }
        //   );
        dispatch(setAllAnswers([]));
        const oldValues = cloneObject(selected_assessment_data);
        dispatch(
          setSelectedAssessmentData({
            ...oldValues,
            // elapsedTime: studentHomeworkResp.data?.attemptSpentTimeInSecs || 0, // Update the elapsed time in Redux
            elapsedTime: updatedElapsedTime, // Update the elapsed time in Redux
          })
        );
        const updatedResponse = new AssessmentStudentResponseV2(
          cloneObject(activeHomeworkStudentResp)
        );
        updatedResponse.responses = responses; // local responses just submitted
        updatedResponse.attemptSpentTimeInSecs = updatedElapsedTime;
        // selected_homework_data?.elapsedTime;
        dispatch(
          setActiveAssessmentStudentResponse({
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            response: updatedResponse,
          })
        );
        const isLastQuestion = activeQuestionIndex === totalQuestions - 1;
        setIsPaused(false);
        if (!isLastQuestion) {
          setSplitText(undefined);
          setEndTestPopup(false);
          // Check if we are at the boundary of the current section
          const isLastOfSection = checkIfLastQuestionOfSection(
            activeQuestionIndex,
            activeSectionNo,
            sectionRanges,
            sections
          );

          if (isLastOfSection) {
            // Find the next section in the array
            const currentSectionIdx = sections.findIndex(
              (s) => s.sectionNo === active_section_number
            );
            const nextSection = sections[currentSectionIdx + 1];

            if (nextSection && nextSection.sectionNo) {
              dispatch(setActiveSectionNumber(nextSection.sectionNo));
            }
          }
          setActiveQuestionId(sortedQuestionIds[activeQuestionIndex + 1]);
        } else if (isLastQuestion && !fromPagination) {
          setIsLastQuestionAttempted(true);
          setSplitText('Do you want to end the test?');
          setEndTestPopup(true);
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Question Submission failed',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          label: `${
            err.rawMessage
              ? err.rawMessage?.replace(/.*\]\s*/, '').trim()
              : err.message
              ? err.message.replace(/.*\]\s*/, '').trim()
              : 'Assessment Submission Failed'
          }`,
          variant: 'error',
          open: true,
        })
      );
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
  const getLatestAnswer = (questionId: string) => {
    const responseObj = activeAssessmentStudentResponse?.responses?.find(
      (e) => e.questionId === questionId
    );

    const history = responseObj?.responses || [];

    // Use the new helper to get a definitive snapshot of the question state
    const historyState = getLastSignificantState(history);

    // If the timeline shows the last significant action was an answer, return it.
    // If it was 'cleared' or 'null', return null.
    if (historyState.status === 'lastAnswered') {
      return historyState.answer;
    }

    return null;
  };

  const handleClearAnswer = async () => {
    if (!activeQuestionId || !assessmentId || isNaN(Number(assessmentId)))
      return;
    if (!active_assessment_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    setIsPaused(true); // Stop timer and prevent navigation clicks
    dispatch(setAllAnswers([])); // Clear local UI state immediately
    setClearNonce((prev) => prev + 1);

    const prevStudentResp = new AssessmentStudentResponseV2(
      cloneObject(activeAssessmentStudentResponse)
    );
    const activeHomeworkStudentResp = new AssessmentStudentResponseV2(
      cloneObject(activeAssessmentStudentResponse)
    );
    // Create clear attempt (recorded as an explicit user action)
    const clearAttempt = new ContentAttempt({
      startTime: startTime,
      endTime: Timestamp.fromDate(new Date()),
      answer: [],
      isClearAnswer: true,
    });
    // Preserve history for all questions; update only the active question
    const updatedResponses = sortedQuestionIds.map((id) => {
      const sectionNo = taskQuestionInfo.find(
        (val) => val.questionId === id
      )?.sectionNo;
      const existing = activeHomeworkStudentResp.responses.find(
        (r) => r.questionId === id
      );
      if (existing) {
        if (!existing.sectionNo) {
          existing.sectionNo = sectionNo;
        }
      }
      const questionTaskId = taskQuestionInfo.find(
        (q) => q.questionId === id
      )?.questionTaskId;

      if (id !== activeQuestionId) {
        return (
          existing ||
          new StudentAssessmentResponse({
            questionId: id,
            questionTaskId,
            responses: [] as ContentAttempt[],
            isAttempted: false,
            sectionNo: sectionNo,
          })
        );
      }
      const updated = existing
        ? cloneObject(existing)
        : new StudentAssessmentResponse({
            questionId: id,
            questionTaskId,
            responses: [] as ContentAttempt[],
            isAttempted: false,
            sectionNo: sectionNo,
          });

      updated.responses = [...(updated.responses || []), clearAttempt];
      updated.isAttempted = false;
      return updated;
    });

    // Update the Question Status Array
    const updatedStatusArray = [...questionStatusArray];
    if (activeQuestionIndex >= 0) {
      updatedStatusArray[activeQuestionIndex] = {
        ...updatedStatusArray[activeQuestionIndex],
        statusInfo: IQuestionStatus.NotAttempted,
      };
      setQuestionStatusArray(updatedStatusArray);
    }

    // Optimistically update Redux so `getLatestAnswer` immediately reflects CLEAR
    const finalFullResponse = cloneObject(activeHomeworkStudentResp);
    finalFullResponse.responses = updatedResponses;
    dispatch(
      setActiveAssessmentStudentResponse({
        assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
        response: finalFullResponse,
      })
    );
    try {
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
            responses: updatedResponses,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_assessment_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeAssessmentContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_assessment_data?.elapsedTime
              : undefined,
          }
        );

      if (hwSubmitResponse.status !== 200) {
        dispatch(
          setActiveAssessmentStudentResponse({
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            response: prevStudentResp,
          })
        );
        dispatch(
          setToastInfo({
            label: 'Failed to clear answer. Please try again.',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err: any) {
      // Revert optimistic redux update if API fails
      dispatch(
        setActiveAssessmentStudentResponse({
          assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
          response: prevStudentResp,
        })
      );
      dispatch(
        setToastInfo({
          label: err?.message || 'Failed to clear answer. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setIsPaused(false);
    }
  };

  const skipQuestionClickHandler = async (questionIndex: number) => {
    await handleNonSeqNext(true);
    const isLastQuestion = questionIndex === totalQuestions - 1;
    if (isLastQuestion) {
      setActiveQuestionId(sortedQuestionIds[0]);
    } else setActiveQuestionId(sortedQuestionIds[questionIndex + 1]);
  };

  // -------------------- Section-wise summaries --------------------

  const sections = activeAssessmentContent?.multiSubjectSections || [];

  const sectionRanges = useMemo(() => {
    const ranges: { start: number; end: number }[] = [];
    let current = 0;
    sections.forEach((section) => {
      const count = section.numberOfQuestion || 0;
      ranges.push({ start: current, end: current + count });
      current += count;
    });
    return ranges;
  }, [sections]);

  const getQuestionIndexBySection = (
    sectionNo: number,
    questionNoInSection: number = 1 // default first question of section
  ): number => {
    const sectionIndex = sections.findIndex(
      (sec) => sec.sectionNo === sectionNo
    );
    if (sectionIndex === -1) return -1;
    const range = sectionRanges[sectionIndex];
    if (!range) return -1;
    const index = range.start + (questionNoInSection - 1);
    // safety check
    if (index < range.start || index >= range.end) return -1;
    return index;
  };

  const getCurrentPageForSection = (sectionIndex: number): number => {
    if (activeQuestionIndex < 0) return 1;
    const range = sectionRanges[sectionIndex];
    if (!range) return 1;
    if (activeQuestionIndex < range.start || activeQuestionIndex >= range.end) {
      return 1;
    }
    return activeQuestionIndex - range.start + 1;
  };

  const handleSectionPageChange = async (
    sectionIndex: number,
    page: number,
    sectionNo?: number
  ) => {
    const range = sectionRanges[sectionIndex];
    if (!range) return;
    const globalIndex = range.start + (page - 1);
    if (globalIndex >= 0 && globalIndex < sortedQuestionIds.length) {
      const nextQuestionId = sortedQuestionIds[globalIndex];
      // Prevent showing stale local answer state from previous question.
      if (nextQuestionId !== activeQuestionId) {
        // user is actually navigating to a different question
        await handleNonSeqNext(true, true); // Save work on current question
        dispatch(setAllAnswers([])); // Reset UI for the next question
        setActiveQuestionId(nextQuestionId); // Switch to the new question
      }
    }
    if (sectionNo) {
      dispatch(setActiveSectionNumber(sectionNo));
    }
  };

  useEffect(() => {
    setSelectedFunction(() => onBackClick);
    setSelectedLogoClickHandler(() => onLogoClick);
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [
    activeQuestionId,
    selected_assessment_data?.elapsedTime,
    activeAssessmentStudentResponse?.responses
      .map((resp) => resp.responses.length)
      .join(','),
  ]);

  useEffect(() => {
    // (async () => {
    //   await aiHomeworkOpenEvent({
    //     homeworkId: selected_homework_data?.homeworkId,
    //     questionIds: selected_homework_data?.questionIds,
    //     isResume: sessionMode === '3' ? true : false,
    //     isReattempted: sessionMode === '2' ? true : false,
    //     homeworkType: activeHomeworkContent?.assessment?.taskType,
    //     isOffline: isOffline,
    //   });
    // })();
    if (NON_SEQUENTIAL) {
      const newStatusArray = [...questionStatusArray];
      const qIndex = getQuestionIndexBySection(activeSectionNo, 1);
      setActiveQuestionId(sortedQuestionIds[qIndex]);
      dispatch(setAllAnswers([]));
      if (activeAssessmentStudentResponse?.responses) {
        activeAssessmentStudentResponse.responses.forEach((ans, i) => {
          const qIndex = sortedQuestionIds.findIndex(
            (val) => val === ans.questionId
          );

          if (qIndex !== -1) {
            //mark attempted if any attempt has answer
            const historyState = getLastSignificantState(ans.responses);
            newStatusArray[qIndex] = {
              ...newStatusArray[qIndex],
              statusInfo:
                historyState.status === 'lastAnswered'
                  ? IQuestionStatus.Attempted
                  : IQuestionStatus.NotAttempted,
            };
          }
        });
        setQuestionStatusArray(newStatusArray);
      }
    } else {
      // if (activeAssessmentStudentResponse?.responses) {
      //   const attemptedQuestionIds = activeAssessmentStudentResponse.responses
      //     .map((val) => (val.isAttempted && val.questionId) || '')
      //     .filter((val) => val !== '');
      //   const filteredQuestionIds = sortedQuestionIds.filter(
      //     (val) => !attemptedQuestionIds.includes(val)
      //   );
      //   // const lastQuestionAttempted =
      //   //   activeHomeworkStudentResponse.responses.length;
      //   if (filteredQuestionIds.length > 0) {
      //     activeAssessmentStudentResponse.responses.forEach((ans, i) => {
      //       const qIndex = sortedQuestionIds.findIndex(
      //         (val) => val === ans.questionId
      //       );
      //       questionStatusArray[qIndex].statusInfo = IQuestionStatus.Attempted;
      //     });
      //     setQuestionStatusArray(questionStatusArray);
      //     setActiveQuestionId(filteredQuestionIds[0]);
      //   }
      // }
    }

    return () => {
      exitFullScreen();
    };
  }, []);
  console.log('latestAnswer allanswer', all_answers);
  useEffect(() => {
    // setAnswers([]);
    // dispatch(setAllAnswers([]));
    if (activeQuestionId) {
      const latestAnswer = getLatestAnswer(activeQuestionId);
      console.log('latestAnswer', latestAnswer, activeQuestionId);
      if (latestAnswer) {
        dispatch(setAllAnswers(latestAnswer)); // assuming setAllAnswers updates the state for `all_answers`
      } else {
        dispatch(setAllAnswers([])); // reset if no previous answer
      }
    }
    setStartTime(Timestamp.fromDate(new Date()));
    // Disabled so that we don't show previous ans
    // const quesResponse = activeHomeworkStudentResponse?.responses.find(resp => resp.questionId == activeQuestionId);
    // setAnswers(new ContentAttempt(getLastAttemptedResponse(quesResponse?.responses)).answer);

    // (async () => {
    //   if (sessionMode === '3' && resumeQueOpenTrigger) {
    //     await aiQuestionOpenEvent({
    //       homeworkId: selected_homework_data?.homeworkId,
    //       questionId: activeQuestionId,
    //       isOffline: isOffline,
    //     });
    //   } else if (sessionMode !== '3') {
    //     await aiQuestionOpenEvent({
    //       homeworkId: selected_homework_data?.homeworkId,
    //       questionId: activeQuestionId,
    //       isOffline: isOffline,
    //     });
    //   }
    //   setResumeQueOpenTrigger(true);
    // })();
  }, [activeQuestionId]);
  useEffect(() => {
    (async () => {
      if (isSubmitting) {
        await handleNonSeqNext();
        setIsSubmitting(false);
      }
    })();
  }, [isSubmitting]);

  const createResponses = () => {
    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      cloneObject(activeAssessmentStudentResponse)
    );

    return sortedQuestionIds.map((questionId) => {
      // 1. Find existing response by ID ONLY
      const existingResponse = activeHomeworkStudentResp.responses.find(
        (resp) => resp.questionId === questionId
      );
      const sectionNo = taskQuestionInfo.find(
        (val) => val.questionId === questionId
      )?.sectionNo;
      // 2. If it's NOT the active question, keep it as is or return a blank shell
      if (questionId !== activeQuestionId) {
        if (existingResponse) {
          if (!existingResponse.sectionNo) {
            existingResponse.sectionNo = sectionNo;
          }
          return existingResponse;
        }

        const questionTaskId = taskQuestionInfo.find(
          (val) => val.questionId === questionId
        )?.questionTaskId;

        return new StudentAssessmentResponse({
          questionId: questionId,
          questionTaskId: questionTaskId,
          responses: [],
          isAttempted: false,
          sectionNo: sectionNo,
        });
      }

      // 3. LOGIC FOR ACTIVE QUESTION
      const history = existingResponse?.responses || [];

      const currentQuestion = selectedQuestionContent;

      const lastAnswer = getLatestAnswer(activeQuestionId);

      let finalAnswer: string[] = [];

      const textTypes = [
        Question_QuestionEnum.QUESTION_TYPE_FIB,
        Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_LONG,
      ];

      if (
        currentQuestion?.questionType === undefined ||
        !textTypes.includes(currentQuestion.questionType)
      ) {
        // Only assign if NOT a text-based question
        finalAnswer = all_answers;
      }

      // Logic: Fallback to history only if UI is empty AND history provides an answer
      // (If history was cleared, lastAnswer will be null, so finalAnswer stays [])
      if (finalAnswer.length === 0 && lastAnswer) {
        finalAnswer = lastAnswer;
      }
      const quesAttempt = new ContentAttempt({
        startTime: startTime,
        endTime: Timestamp.fromDate(new Date()),
        answer: finalAnswer,
        isClearAnswer: false, // This is a navigation/save action, not a 'Clear' button click
      });

      const updatedResponse = existingResponse
        ? cloneObject(existingResponse)
        : new StudentAssessmentResponse({
            questionId: activeQuestionId,
            questionTaskId: taskQuestionInfo.find(
              (q) => q.questionId === activeQuestionId
            )?.questionTaskId,
            responses: [],
            isAttempted: false,
            sectionNo: sectionNo,
          });

      // Collate the history by appending the new attempt
      updatedResponse.responses = [...history, quesAttempt];
      updatedResponse.isAttempted = finalAnswer.length > 0;

      return updatedResponse;
    });
  };
  const submitBackHandler = async (isBackClicked?: boolean) => {
    let studentResponse: StudentAssessmentResponse[];
    if (NON_SEQUENTIAL) {
      studentResponse = createResponses();
    } else {
      //not required in multisubject flow
      const quesAttempt = new ContentAttempt({
        startTime: startTime,
        endTime: Timestamp.fromDate(new Date()),
        answer: [],
      });
      const questionTaskId = taskQuestionInfo.find(
        (val) => val.questionId === activeQuestionId
      )?.questionTaskId;
      const responseToSubmit = new StudentAssessmentResponse({
        // homeworkId: (homework_id ? BigInt(homework_id) : undefined),
        questionId: activeQuestionId,
        questionTaskId: questionTaskId,
        responses: [quesAttempt],
        isAttempted: false,
      });

      const activeAssessmentStudentResp = new AssessmentStudentResponse(
        activeAssessmentStudentResponse
      );
      const quesResponseIndex = activeAssessmentStudentResp.responses.findIndex(
        (resp) => resp.questionId === activeQuestionId
      );
      if (quesResponseIndex >= 0) {
        activeAssessmentStudentResp.responses[quesResponseIndex].responses.push(
          quesAttempt
        );
      } else {
        activeAssessmentStudentResp.responses.push(responseToSubmit);
      }
      studentResponse = activeAssessmentStudentResp.responses;
    }

    console.log('submit:', studentResponse);
    try {
      setIsPaused(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            assessmentAttemptStatus: isBackClicked
              ? TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS
              : TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_TIMED_OUT,
            responses: studentResponse,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_assessment_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeAssessmentContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_assessment_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse) {
        if (!isBackClicked) {
          setTimeupPopup(true);
        }
      } else {
        if (!isBackClicked) {
          dispatch(
            setToastInfo({
              label: 'Something went wrong. Please try again.',
              variant: 'error',
              open: true,
            })
          );
          navigate(HOMEWORK_STUDENT_DASHBOARD);
        } else {
          dispatch(
            setToastInfo({
              label: 'Something went wrong. ',
              variant: 'error',
              open: true,
            })
          );
        }
      }
    } catch (err: any) {
      if (!isBackClicked) {
        dispatch(
          setToastInfo({
            label: 'Something went wrong. Please try again.',
            variant: 'error',
            open: true,
          })
        );
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      } else {
        dispatch(
          setToastInfo({
            label: 'Something went wrong. ',
            variant: 'error',
            open: true,
          })
        );
      }
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
      console.log(err);
    }
  };
  const assessmentSubmit = async () => {
    if (isLoading) return;
    if (
      activeQuestionIndex < 0 ||
      !assessmentId ||
      isNaN(Number(assessmentId))
    ) {
      return;
    }
    if (!active_assessment_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    let activeStudentResponse: StudentAssessmentResponse[];

    if (isLastQuestionAttempted) {
      activeStudentResponse = new AssessmentStudentResponse(
        activeAssessmentStudentResponse
      ).responses;
    } else {
      activeStudentResponse = createResponses();
    }

    console.log('responses', isLastQuestionAttempted, activeStudentResponse);
    setIsLoading(true);
    try {
      setIsPaused(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED,
            responses: activeStudentResponse,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_assessment_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeAssessmentContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_assessment_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse.status === 200) {
        const activeHomeworkAttemptResult = hwSubmitResponse.attemptResult;
        dispatch(
          setActiveAssessmentAttemptResult({
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            result: activeHomeworkAttemptResult,
          })
        );
        setIsPaused(true);
        dispatch(setActiveAssessmentSessionId(undefined));
        navigate(`${FA_SUBMITTED}?assessmentId=${assessmentId}`);
      } else {
        dispatch(
          setToastInfo({
            label: 'Assessment Submission failed',
            variant: 'error',
            open: true,
          })
        );
      }
      setIsPaused(false);
    } catch (err: any) {
      setIsPaused(false);
      dispatch(
        setToastInfo({
          label: `${
            err.rawMessage
              ? err.rawMessage?.replace(/.*\]\s*/, '').trim()
              : err.message
              ? err.message.replace(/.*\]\s*/, '').trim()
              : 'Assessment Submission Failed'
          }`,
          variant: 'error',
          open: true,
        })
      );
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (
    !activeAssessmentContent ||
    !activeQuestionId ||
    !selectedQuestionContent
  ) {
    return null;
  }

  const getSectionDisplayTitle = (section?: MultiSubjectSection): string => {
    if (!section) return '';

    const subjectName = getSubjectNameFromEnum(section.subject)?.trim();
    const sectionTitle = section.sectionTitle?.trim();

    if (subjectName && sectionTitle) {
      return `${subjectName} (${sectionTitle})`;
    }

    return subjectName || sectionTitle || '';
  };

  if (isLoading) return <Loader />;

  return (
    <Grid
      container
      spacing={0}
      sx={{ ...styles.root, opacity: showFullScreenPopup ? 0 : 1 }}
    >
      <Grid container sx={styles.heading}>
        <Grid
          item
          xs={12}
          md={8.5}
          display="flex"
          gap={isMobile ? pxToRem(2) : pxTovW(20)}
          alignItems="center"
          justifyContent={isMobile ? 'space-between' : 'flex-start'}
        >
          <Typography
            variant="h2"
            sx={{
              width: { xs: 'max-content' },
              maxWidth: { xs: '60%', md: '80%' },
              wordWrap: 'break-word',
            }}
          >
            {getSectionDisplayTitle(
              sections.find((e) => e.sectionNo === activeSectionNo)
            )}
          </Typography>
          <Box sx={{ display: { xs: 'block' } }}>
            {checkFeatureData({
              assignmentConfigInfo:
                activeAssessmentContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed && (
              <HomeworkTimer
                variant="small"
                initialTime={initialTimerValue}
                onTimeUpdate={handleTimeUpdate}
                isPaused={isPaused}
                onTestEnd={() => {
                  submitBackHandler();
                }}
                noLabel={true}
              />
            )}
          </Box>
        </Grid>
        {!isMobile && (
          <Grid item xs={12} md={3.5}>
            <Box sx={styles.submitButtonContainer}>
              <Button
                sx={styles.submitButton}
                onClick={() => setEndTestPopup(true)}
              >
                End Test
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid container sx={{ padding: 0 }}>
        <Grid
          item
          xs={12}
          md={3.5}
          sx={{
            order: { xs: 1, md: 2 },
          }}
        >
          <Box sx={styles.questionNumberBox}>
            <Box sx={{ width: '100%' }}>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                paddingY={1}
              >
                <Box
                  width={'100%'}
                  display={'flex'}
                  justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                  alignItems={'center'}
                  gap={1}
                >
                  <Typography variant="h5" color="neutral.duskySteel">
                    Completed{' '}
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {getCompletionPercentage(questionStatusArray)}
                    {'%'}
                  </Typography>
                </Box>

                <Box display={{ xs: 'block', md: 'none' }}>
                  <Button
                    variant="contained"
                    color="success"
                    sx={{
                      width: 'max-content',
                      color: 'white',
                      fontSize: { xs: pxToRem(10) },
                    }}
                    onClick={() => setQuestionSummaryPopup(true)}
                  >
                    Questions Summary
                  </Button>
                </Box>
              </Box>
              <ActiveProgressbar
                percentage={getCompletionPercentage(questionStatusArray)}
              />
            </Box>
            {/* Section-wise summaries */}
            <Box
              sx={{
                display: 'flex',
                gap: pxToRem(10),
                justifyContent: 'flex-start',
                alignItems: 'center',
                border: '2px solid #55ACFF ',
                height: { md: pxTovW(60) },
                background: '#F5FAFF',
                borderRadius: '2px',
                p: 2,
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
              onClick={() => setOverallSectionPopup(true)}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: { xs: pxToRem(33), md: pxTovW(40) },
                  height: { xs: pxToRem(33), md: pxTovW(40) },
                  borderRadius: '50%',
                  background: 'white',
                }}
              >
                <IconWrapper
                  name="question-blue"
                  size="md"
                  parentFolder="icons"
                  type="png"
                  customSx={{
                    width: { xs: pxToRem(20), md: pxTovW(25) },
                    height: { xs: pxToRem(20), md: pxTovW(25) },
                  }}
                />
              </Box>
              <Typography variant="cardText" fontFamily="Poppins">
                Overall Summary
              </Typography>
              <KeyboardArrowRightIcon
                sx={{
                  marginLeft: 'auto',
                }}
              />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {sections.map((section, index) => {
                const range = sectionRanges[index];
                const sectionTotal = (range?.end ?? 0) - (range?.start ?? 0);
                const sectionStatusArray =
                  range && sectionTotal > 0
                    ? questionStatusArray.slice(range.start, range.end)
                    : [];

                return (
                  <Box key={index} sx={{ mt: 2 }}>
                    {/* <Typography variant="h6" mb={1}>
                    {section.sectionTitle || section.sectionName || 'Section'}
                  </Typography> */}
                    {sectionTotal > 0 && (
                      <QuestionsSummaryPagination
                        currentPage={getCurrentPageForSection(index)}
                        totalPages={sectionTotal}
                        questionStatusArray={sectionStatusArray}
                        sectionNo={section.sectionNo}
                        activeSectionNo={activeSectionNo}
                        onPageChange={(page) =>
                          handleSectionPageChange(
                            index,
                            page,
                            section.sectionNo
                          )
                        }
                        sectionInfo={section}
                        learnSubjects={userInfo?.learnSubjects}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8.5} sx={{ order: { xs: 2, md: 1 } }}>
          <Box sx={styles.questionBox}>
            <QuestionContainerWithSolution
              key={`${activeQuestionId}-${clearNonce}`}
              userAttemptedAnswer={
                all_answers && all_answers.length
                  ? all_answers
                  : getLatestAnswer(activeQuestionId) ?? // Check if the answer is not empty
                    undefined
              }
              // questionNumber={activeQuestionIndex + 1}
              questionNumber={renderQuestionNumber(
                activeQuestionIndex,
                activeSectionNo,
                sectionRanges,
                sections
              )}
              question={new Question(selectedQuestionContent)}
              handleAnswerChange={handleAnswerChange}
              handleNext={handleNonSeqNext}
              showNextButton
              showHintIcon
              homeworkAttempt={true}
              scanClickHandler={scanClickHandler}
              hiddenScanButton={isProctoringEnabled}
              selected_resource_info={selected_resource_info}
              disableInput={isSubmitting}
              eventCollection={{
                hintBoxOpen: async () => {
                  await interactionEvent({
                    url: '',
                    context: 'popup',
                    name: 'CLUE',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                },
                hintBoxOk: async () => {
                  await interactionEvent({
                    url: '',
                    context: 'clue',
                    name: 'OK',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                },
                answerSelection: async () => {
                  await interactionEvent({
                    url: '',
                    context: 'question',
                    name: 'ANSWER_SELECTION',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                },
              }}
              imageResizeEnabled={true}
              questionTypeDisplay={true}
              showQuestionStats
              clearAnswerDisplay={
                activeAssessmentContent?.multiSubjectSections.find(
                  (e) => e.sectionNo === activeSectionNo
                )?.markingSchemeType === MarkingSchemeEnum.MARKING_SCHEME_JEE
                  ? true
                  : false
              }
              isClearAnswerDisabled={
                isPaused ||
                ((!all_answers || all_answers.length === 0) &&
                  getLastSignificantState(
                    activeAssessmentStudentResponse?.responses?.find(
                      (r) => r.questionId === activeQuestionId
                    )?.responses || []
                  ).status !== 'lastAnswered')
              }
              clearAnswerClickHandler={handleClearAnswer}
              hideBloomLevel={
                activeAssessmentContent?.multiSubjectSections.find(
                  (e) => e.sectionNo === activeSectionNo
                )?.markingSchemeType === MarkingSchemeEnum.MARKING_SCHEME_JEE
                  ? true
                  : false
              }
            />
          </Box>
          {activeQuestionIndex !== totalQuestions - 1 ? (
            <Box component="div" display={'flex'} justifyContent={'center'}>
              <LinkButton
                textColor={'primary.main'}
                onClick={() => skipQuestionClickHandler(activeQuestionIndex)}
              >
                Skip Question
              </LinkButton>
            </Box>
          ) : (
            <></>
          )}
          <Box
            component="div"
            display={{ xs: 'flex', md: 'none' }}
            justifyContent={'center'}
            p={2}
          >
            {NON_SEQUENTIAL && (
              <Box sx={styles.submitButtonContainer}>
                <Button
                  sx={styles.submitButton}
                  onClick={() => setEndTestPopup(true)}
                >
                  End Test
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <ImageUploadPopup
        answerSource="multisubjectFA"
        open={openUploadPopup}
        handleClose={() => setOpenUploadPopup(false)}
        studentId={userInfo?.studentProfileId}
        homeworkId={(assessmentId ? BigInt(assessmentId) : undefined)}
        sessionId={active_assessment_session_id}
        questionId={selectedQuestionContent?.questionId}
        handleAnswerChange={handleAnswerChange}
      />
      <OverallSummaryPopup
        open={overallSectionPopup}
        handleClose={() => setOverallSectionPopup(false)}
        data={getSummaryData(sections, sectionRanges, questionStatusArray)}
      />
      <Modal
        open={questionSummaryPopup}
        onClose={() => setQuestionSummaryPopup(false)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            // height: '100vh',
            width: '100vw',
            padding: { xs: pxToRem(10), md: pxToRem(0) },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderRadius: pxToRem(10),
              background: 'white',
              display: 'flex',
              flexDirection: 'column',
              // justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              p: 1,
            }}
          >
            {sections.map((section, index) => {
              const range = sectionRanges[index];
              const sectionTotal = (range?.end ?? 0) - (range?.start ?? 0);
              const sectionStatusArray =
                range && sectionTotal > 0
                  ? questionStatusArray.slice(range.start, range.end)
                  : [];

              return (
                <Box sx={{ mt: 0, width: '95%' }} key={index}>
                  {sectionTotal > 0 && (
                    <QuestionsSummaryPagination
                      currentPage={getCurrentPageForSection(index)}
                      totalPages={sectionTotal}
                      questionStatusArray={sectionStatusArray}
                      sectionNo={section.sectionNo}
                      activeSectionNo={activeSectionNo}
                      onPageChange={(page) =>
                        handleSectionPageChange(index, page, section.sectionNo)
                      }
                      sectionInfo={section}
                      learnSubjects={userInfo?.learnSubjects}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
          <Box>
            <Button
              onClick={() => setQuestionSummaryPopup(false)}
              sx={{ p: pxToRem(10) }}
            >
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Modal>
      <ActionPopUpSingleButton
        fontSmall
        open={timeupPopup}
        iconName="info_red"
        popupText="The allotted time for the assessment has ended."
        buttontext="Ok"
        ctaHandler={() => {
          setTimeupPopup(false);
          navigate(HOME);
        }}
        background="#0AA34F"
        textcolor="white"
      />

      <ActionsPopup
        open={endTestPopup}
        handleClose={() => {
          setEndTestPopup(false);
        }}
        closeIcon
        fontSmall
        iconName="search"
        popupText={'You have answered all the questions.'}
        splitText={splitText ? splitText : 'Would you like to submit the test?'}
        popupTextColor="primary.main"
        splitTextColor="black"
        yesClickHandler={assessmentSubmit}
        noClickHandler={async () => {
          setSplitText(undefined);
          setEndTestPopup(false);
        }}
        noText={splitText ? 'REVIEW TEST' : 'GO BACK TO TEST'}
        yesText="END TEST"
        customComponent={
          getUnattemptCount(questionStatusArray) > 0
            ? EndTestPopupContent({
                not_answered: getUnattemptCount(questionStatusArray),
                total: totalQuestions,
              })
            : undefined
        }
        buttonVariant="greenContained"
      />
      <ProctoringPopups
        showFullScreenPopup={showFullScreenPopup}
        setShowFullScreenPopup={setShowFullScreenPopup}
        enterFullScreenMode={() => {
          proctoring.fullScreen.requestFullScreen();
          setShowFullScreenPopup(false);
        }}
        showTabSwitchPopup={showTabSwitchPopup}
        setShowTabSwitchPopup={setShowTabSwitchPopup}
        enterTabSwitchMode={() => {
          setShowTabSwitchPopup(false);
        }}
        showMultipleSessionPopup={showMultipleSessionPopup}
        setShowMultipleSessionPopup={setShowMultipleSessionPopup}
        enterMultipleSessionMode={() => {
          setShowMultipleSessionPopup(false);
        }}
        showInactiveSessionPopup={showInactiveSessionPopup}
        setShowInactiveSessionPopup={setShowInactiveSessionPopup}
        enterInactiveSessionMode={() => {
          setShowInactiveSessionPopup(false);
        }}
      />
    </Grid>
  );
};

export default FAActiveHomework;

export function getUnattemptCount(
  questionStatusArray: IQuestionStatusObject[]
): number {
  if (!questionStatusArray.length) return 0;

  const notAttemptedCount = questionStatusArray.filter(
    (q) => q.statusInfo === IQuestionStatus.NotAttempted
  ).length;

  return notAttemptedCount;
}
const checkIfLastQuestionOfSection = (
  globalIndex: number,
  sectionNo: number,
  ranges: { start: number; end: number }[],
  sectionsList: MultiSubjectSection[]
): boolean => {
  // 1. Find the index of the current section in the array
  const sectionIndex = sectionsList.findIndex((s) => s.sectionNo === sectionNo);
  if (sectionIndex === -1) return false;

  // 2. Get the boundary range for this section
  const range = ranges[sectionIndex];
  if (!range) return false;

  // 3. If global index is the last index in that range (end is exclusive, so end - 1)
  return globalIndex === range.end - 1;
};
const renderQuestionNumber = (
  globalIndex: number,
  sectionNo: number,
  ranges: { start: number; end: number }[],
  sectionsList: MultiSubjectSection[]
): number => {
  const sectionIndex = sectionsList.findIndex((s) => s.sectionNo === sectionNo);
  if (sectionIndex === -1) return 0;
  const range = ranges[sectionIndex];
  if (!range) return 0;
  return globalIndex - range.start + 1;
};
